/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import _ from 'lodash';
import visibilityChecker from 'scripts/utils/visibility';
import dtm from 'scripts/services/dtm';
import appConfig from 'scripts/app-config';
import template from './brightcove-video.stache';
import Modernizr from 'modernizr';
import deviceUtil from 'scripts/utils/device';

const brightcoveOptions = _.get(window.globalSettings, 'brightcove');
const accountId = brightcoveOptions.accountId;
const playerId = brightcoveOptions.playerId;
const playerUrl = _.get(appConfig, 'urls.api.brightcove.playerUrl');

const onContextMenu = () => {
    return false;
};
const inWebView = window.globalSettings.inWebView;

can.Component.extend({
    tag: 'brightcove-video',
    template,
    leakScope: false,
    viewModel: {
        accountId,
        playerId,
        id: _.uniqueId('video-'),
        define: {
            autoplay: {
                type: 'boolean',
                value: false,
            },
            pauseOnExitFullscreen: {
                type: 'boolean',
                value: false,
            },
            muted: {
                type: 'boolean',
                value: false,
            },
            noControls: {
                type: 'boolean',
                value: false,
            },
            loop: {
                type: 'boolean',
                value: false,
            },
            videoId: {
                type: 'string',
                value: null,
            },
            continuePlaying: {
                type: 'boolean',
                value: false,
            },
            dtmTrack: {
                type: 'htmlbool',
                value: false,
            },
            dtmVideoName: {
                type: 'string',
                value: '',
            },
            dtmVideoLocation: {
                type: 'string',
                value: '',
            },
            isMobileOrTablet: {
                type: 'boolean',
                get() {
                    return (
                        (deviceUtil.isMobile || deviceUtil.isTablet) &&
                        !inWebView
                    );
                },
            },
            isFullscreen: {
                type: 'boolean',
                value: false,
            },
            disablePip: {
                type: 'boolean',
                value: false,
            },
        },
        enableDtmTrack() {
            this.attr('dtmTrack', true);
        },
        reset() {
            const player = this.attr('player');
            if (player) {
                player.currentTime(0);
            }
        },
        play() {
            let player = this.attr('player');
            if (player) {
                player.play();
            }
        },
        playWithSoundCheck() {
            let player = this.attr('player');
            if (player) {
                Modernizr.on('videoautoplay', (hasAutoplay) => {
                    if (!hasAutoplay) {
                        this.attr('muted', true);
                        player.muted(true);
                    }
                    player.play();
                });
            }
        },
        pause() {
            let player = this.attr('player');
            if (player) {
                player.pause();
            }
        },
        isPlaying() {
            let player = this.attr('player');
            if (player) {
                return Boolean(
                    player.currentTime() > 0 &&
                        !player.paused() &&
                        !player.ended() &&
                        player.readyState() > 2,
                );
            }
            return false;
        },
        isPaused() {
            let player = this.attr('player');
            if (player) {
                return Boolean(player.currentTime() > 0 && player.paused());
            }
            return false;
        },
    },
    events: {
        inserted() {
            this.initPlayer();
            this.viewModel.bind('videoId', () => {
                this.updateVideo();
            });
            this.relaunchVideo = function(event, isVisible) {
                if (isVisible && this.viewModel.attr('continuePlaying')) {
                    this.viewModel.attr('continuePlaying', false);
                    this.viewModel.play();
                }
                if (!isVisible && this.viewModel.isPlaying()) {
                    this.viewModel.attr('continuePlaying', true);
                    this.viewModel.pause();
                }
            }.bind(this);
            visibilityChecker.bind('active', this.relaunchVideo);
            this.element.find('video').on('contextmenu', onContextMenu);
            this.viewModel.attr('$el', this.element);
        },

        removed() {
            visibilityChecker.unbind('active', this.relaunchVideo);
            this.element.find('video').off('contextmenu', onContextMenu);
        },

        updateVideo() {
            const player = this.viewModel.attr('player');
            const videoId = this.viewModel.attr('videoId');

            player.catalog.getVideo(videoId, (error, video) => {
                if (error) {
                    return console.error(`Video ${videoId} can not be loaded`);
                }

                this.viewModel.dispatch({
                    type: 'metadata',
                    data: video,
                });

                player.catalog.load(video);

                this.viewModel.dispatch('videoplay');
                if (this.viewModel.attr('dtmTrack')) {
                    const videoName =
                        this.viewModel.attr('dtmVideoName') || video.name || '';
                    dtm.trackVideoLaunch(
                        videoName,
                        this.viewModel.attr('dtmVideoLocation'),
                    );
                }

                if (this.viewModel.attr('autoplay')) {
                    this.viewModel.play();
                }
            });
        },
        getPlayerOptions() {
            return {
                controlBar: {
                    children: [
                        { name: 'liveDisplay' },
                        { name: 'playToggle' },
                        { name: 'currentTimeDisplay' },
                        { name: 'timeDivider' },
                        { name: 'durationDisplay' },
                        { name: 'progressControl' },
                        { name: 'volumeMenuButton' },
                        { name: 'fullscreenToggle' },
                    ],
                },
                inactivityTimeout: 0,
                preload: true,
                autoplay: this.viewModel.attr('autoplay'),
                muted: this.viewModel.attr('muted'),
                loop: this.viewModel.attr('loop'),
            };
        },
        initPlayer() {
            if (this.viewModel.attr('player')) {
                return;
            }

            let video = this.element.find('video');
            const playerOptions = this.getPlayerOptions();

            if (deviceUtil.isIos()) {
                playerOptions.playsinline = true;
                playerOptions.controls = true;
            }

            if (video && video.length) {
                const accountId = this.viewModel.attr('accountId');
                const playerId = this.viewModel.attr('playerId');
                const self = this;

                $.getScript(
                    can.sub(playerUrl, {
                        accountId,
                        playerId,
                    }),
                ).done(() => {
                    videojs
                        .getPlayer(video[0], this.getPlayerOptions())
                        .ready(function() {
                            self.viewModel.attr('player', this);
                            self.viewModel.dispatch('brightcoveready', [
                                self.viewModel,
                            ]);

                            if (playerOptions.muted) {
                                this.muted(true);
                            }

                            this.controlBar.addClass('swiper-no-swiping');

                            this.on('fullscreenchange', () => {
                                self.viewModel.dispatch('fullscreenchange', [
                                    this.isFullscreen(),
                                ]);
                            });

                            this.on('ended', () => {
                                self.viewModel.dispatch({ type: 'ended' });
                            });

                            this.on('play', function(evt) {
                                self.viewModel.dispatch('brightcoveplay', [
                                    self.viewModel,
                                ]);
                                self.viewModel.dispatch('play', evt);
                            });

                            this.on('pause', () => {
                                self.viewModel.dispatch('videopause');
                            });

                            this.on('seeking', () => {
                                self.viewModel.dispatch('videoseeking');
                            });

                            this.on('playing', () => {
                                self.viewModel.dispatch('videoplaying');
                            });

                            this.on('error', () => {
                                self.viewModel.dispatch('videoerror');
                            });

                            self.updateVideo();
                        });
                });
            }
        },

        handleAutoplayCheck(player) {
            if (player.autoplay() && !player.muted()) {
                Modernizr.on('videoautoplay', (hasAutoplay) => {
                    if (!hasAutoplay) {
                        this.viewModel.attr('muted', true);
                        player.muted(true);
                        player.play();
                    }
                });
            }
        },
    },
});
